<template>
  <div :class="classes">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      :nudge-right="40"
      transition="scale-transition"
      offset-y
      :min-width="min_width"
    >
      <template v-slot:activator="{ attrs }">
        <TextField
          v-model="text_date"
          v-mask="date_mask"
          :label="label"
          prepend-inner-icon="mdi-calendar"
          v-bind="attrs"
          blue_icon
          :dense="dense"
          :hide-details="hide_details"
          :error-messages="error_message"
          :rules="rules"
          :clearable="clearable"
          :require="required"
          @keyup="handle_key_up"
          @blur="text_date_changed"
          @click:clear="clear"
          @input="handle_input"
          @click:prepend-inner="menu = true"
          :small_error="small_info"
          :disabled="disabled"
        />
      </template>
      <v-date-picker
        v-bind="$attrs"
        v-on="$listeners"
        v-model="date_model"
        :max="max"
        first-day-of-week="1"
        color="accent"
        :title-date-format="title_range_date_format"
        width="336px"
      />
    </v-menu>
  </div>
</template>

<script lang="ts">
import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator';
import TextField from '../inputs/text-field/TextField.vue';

@Component({
  components: {
    TextField,
  },
})
export default class DatepickerRange extends Vue {
  @Prop({ type: Boolean, default: undefined })
  disabled!: boolean | undefined

  mounted() {
    if (this.date_prop) {
      this.on_prop_date_change();
    }
  }

  @Prop({ type: String, default: '' })
  type_date!: string;

  @Prop({ type: String, default: 'Дата' })
  label!: string;

  @Prop({ type: Array, default: () => [] })
  date_prop!: string[];

  @Prop({ type: Boolean, default: false })
  small_calendar!: boolean;

  @Prop({ type: String, default: '' })
  error!: string;

  @Prop({
    type: [
      String, Array,
    ],
    default: '',
  })
  classes!: string | [];

  @Prop({ type: Boolean, default: false })
  hide_details!: boolean;

  @Prop({ type: Boolean, default: false })
  clearable!: boolean;

  @Prop({ type: Boolean, default: false })
  required!: boolean;

  @Prop({ type: Boolean, default: false })
  dense!: boolean;

  @Prop({ type: Array, default: () => [] })
  rules!: [];

  @Prop({ type: String, default: '' })
  max!: '';

  @Prop({ type: Boolean, default: false })
  small_info!: boolean;

  @Watch('date_prop')
  on_prop_date_change() {
    this.date = this.parse_date(this.date_prop);
    this.text_date = this.format_date(this.date_prop);
  }

  @Watch('one_date_selected')
  on_one_date_selected() {
    if (this.one_date_selected) {
      this.emit_error(this.err[1]);
    }
  }

  menu = false;

  date: string | string[] = this.is_range ? [] : '';

  text_date = '';

  error_message = '';

  err: string[ ] = [
    'Невалидная дата',
    'Невалидный период',
  ];

  get is_range() {
    return this.type_date === 'period';
  }

  get date_model() {
    return this.date;
  }

  set date_model(date: string | string[]) {
    this.error_message = '';
    this.date = date;
    this.text_date = this.format_date(date);

    // на бэк отправляем в формате yyyy-mm-dd
    if (this.is_range && this.date.length === 2) {
      this.$emit('date_changed', this.date);
    }
    if (!this.is_range) {
      this.$emit('date_changed', this.date);
    }
  }

  get min_width() {
    return this.small_calendar ? '' : '290px';
  }

  get one_date_selected() {
    return this.is_range && !this.menu && this.date.length === 1;
  }

  // форматирование даты для текстового представления в инпуте

  format_date_item(date: string) {
    if (date.split('.').length > 2) {
      return date;
    }
    const [
      year = '1970', month = '01', day = '01',
    ] = date.split('-');

    return `${day}.${month}.${year}`;
  }

  format_date(date: string | string[]) {
    if (!date || !date.length) {
      return '';
    }
    if (typeof date === 'string') {
      return this.format_date_item(date);
    }
    return date.map((item) => this.format_date_item(item)).join('-');
  }

  get date_mask() {
    switch (this.type_date) {
      case 'month':
        return '##.####';

      case 'period':
        return '##.##.####-##.##.####';

      default:
        return '##.##.####';
    }
  }

  // форматирование даты для передачи бэку в нужном формате

  parse_date(date: string | string[]) {
    if (!this.is_range && !date) {
      return '';
    }
    if (this.is_range && !date.length) {
      return [];
    }
    if (!this.is_range && typeof date === 'string') {
      return date.split('.').reverse().join('-');
    }
    if (this.is_range && typeof date === 'object') {
      return date.map((item: string) => item.split('.').reverse().join('-'));
    }
    return '';
  }

  validate_dates() {
    const dates_formatted = this.text_date.split('-').map((date) => new Date(date));

    return dates_formatted.length === 2;
  }

  validate_text_date(date: string) {
    if (!date) {
      return false;
    }
    const date_arr = date.split('.');

    if (!date_arr.length || date_arr.length <= 2) {
      return false;
    }
    const day = Number(date_arr[0]) || '';
    const month = Number(date_arr[1]) || '';
    const year = Number(date_arr[2]) || '';

    if (!day || !month || !year) {
      return false;
    }
    if (month < 1 || month > 12) {
      return false;
    }
    if (year < 1900) {
      return false;
    }
    if ((day && (day < 0 || day > 31)) || month > 12) {
      return false;
    }
    return true;
  }

  handle_key_up(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      this.text_date_changed();
    }
  }

  text_date_changed() {
    if (this.is_range && this.text_date) {
      const are_dates_valid = this.text_date
        .split('-')
        .map((date) => this.validate_text_date(date));

      if (are_dates_valid.every((item) => item) && this.validate_dates()) {
        this.date = this.parse_date(this.text_date.split('-'));
        this.$emit('date_changed', this.date);
        this.error_message = '';
      } else {
        const err_type = this.validate_dates() ? this.err[0] : this.err[1];

        this.emit_error(err_type);
      }
    } else if (!this.is_range && this.text_date) {
      const is_date_valid = this.validate_text_date(this.text_date);

      if (is_date_valid) {
        this.date = this.parse_date(this.text_date);
        this.$emit('date_changed', this.parse_date(this.date));
      } else {
        this.emit_error(this.err[0]);
      }
    } else {
      const default_date = this.is_range ? [] : '';

      this.$emit('date_changed', default_date);
      this.date = default_date;
      this.error_message = '';
    }
  }

  emit_error(err: string) {
    this.error_message = err;
    this.$emit('update:error', this.parse_date(this.text_date));
  }

  clear() {
    this.date_model = this.is_range ? [] : '';
    this.text_date = '';
    this.error_message = '';
    this.$emit('clear');
  }

  handle_input() {
    this.menu = false;
    this.error_message = '';
  }

  title_range_date_format(dates: string[]) {
    const n = dates.map((n: string) => new Date(n).toLocaleString('ru-RU', {
      // weekday: 'short',
      month: 'short',
      day: 'numeric',
    }))
      .join(' - ');

    return n;
  }
}
</script>
